import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { T, useGT } from 'gt-react';
import {
  RowsIcon,
  SlidersHorizontalIcon,
  UserCircleIcon,
} from '@phosphor-icons/react';
import { ArTasksGroupBySchema, type ArTaskRow } from '@erp/rpc';
import type { ReactTable } from '@/lib/tanstack-table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { DataTableViewOptions } from '@/components/common/data-table/data-table-view-options';
import { DateRangePicker } from '@/components/common/date-pickers/date-range-picker';
import {
  CLEARED_AR_TASKS_FILTERS,
  arTasksDateRange,
  hasArTasksFilters,
} from './arTasksPageState';
import type { ArTasksSearch } from './arTasksSearch';

function toIsoDate(date: Date | undefined): string | undefined {
  return date ? format(date, 'yyyy-MM-dd') : undefined;
}

const NO_GROUPING = 'none';

/** Group by None (the flat list) or Assignee; more groupings fit here later. */
function ArTasksGroupBySelect({
  groupBy,
  onChange,
}: {
  groupBy: ArTasksSearch['groupBy'];
  onChange: (groupBy: ArTasksSearch['groupBy']) => void;
}) {
  const gt = useGT();
  return (
    <Select
      value={groupBy ?? NO_GROUPING}
      onValueChange={(value) => {
        const parsed = ArTasksGroupBySchema.safeParse(value);
        onChange(parsed.success ? parsed.data : undefined);
      }}
    >
      <SelectTrigger
        className="gap-1.5"
        aria-label={gt('Group tasks by', {
          context: 'AR tasks group-by picker, accessible label',
        })}
      >
        <RowsIcon className="size-4" />
        <span className="text-muted-foreground">
          <T context="AR tasks group-by picker prefix, before the chosen grouping">
            Group by:
          </T>
        </span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        <SelectItem value={NO_GROUPING}>
          <T context="AR tasks group-by option, the flat ungrouped list">
            None
          </T>
        </SelectItem>
        <SelectItem value="assignee">
          <T context="AR tasks group-by option, a group per assignee">
            Assignee
          </T>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

/**
 * Search, date range and Assigned to Me on the left; Group by, Assignment
 * Rules and View on the right.
 */
export function ArTasksToolbar({
  table,
  search,
  columnLabels,
  onResetLayout,
  onOpenRules,
  onChange,
}: {
  table: ReactTable<ArTaskRow>;
  search: ArTasksSearch;
  columnLabels: Record<string, string>;
  onResetLayout: () => void;
  /** Omitted for users who can't change task assignment. */
  onOpenRules?: () => void;
  onChange: (updates: Partial<ArTasksSearch>) => void;
}) {
  const gt = useGT();
  return (
    <DataTableToolbar
      table={table}
      searchPlaceholder={gt('Search tasks...', {
        context:
          'AR tasks search input placeholder; matches reference and customer',
      })}
      searchValue={search.search ?? ''}
      onSearchChange={(value) => onChange({ search: value || undefined })}
      onClearFilters={() => onChange(CLEARED_AR_TASKS_FILTERS)}
      hasCustomFilters={hasArTasksFilters(search)}
      actions={
        <>
          <ArTasksGroupBySelect
            groupBy={search.groupBy}
            onChange={(groupBy) => onChange({ groupBy })}
          />
          {onOpenRules && (
            <Button variant="outline" onClick={onOpenRules}>
              <SlidersHorizontalIcon className="mr-1 size-4" />
              <T context="AR tasks button, open assignment rules">
                Assignment Rules
              </T>
            </Button>
          )}
          <DataTableViewOptions
            table={table}
            columnLabels={columnLabels}
            onResetLayout={onResetLayout}
          />
        </>
      }
    >
      <DateRangePicker
        value={arTasksDateRange(search)}
        onChange={(range: DateRange | undefined) =>
          onChange({
            dateFrom: toIsoDate(range?.from),
            dateTo: toIsoDate(range?.to),
          })
        }
        placeholder={gt('Date', {
          context: 'AR tasks date range filter placeholder',
        })}
        className="w-fit data-[empty=true]:text-foreground"
      />
      <Button
        variant={search.assignedToMe ? 'secondary' : 'outline'}
        aria-pressed={Boolean(search.assignedToMe)}
        onClick={() =>
          onChange({ assignedToMe: search.assignedToMe ? undefined : true })
        }
      >
        <UserCircleIcon className="mr-1 size-4" />
        <T context="AR tasks quick filter, tasks assigned to the current user">
          Assigned to Me
        </T>
      </Button>
    </DataTableToolbar>
  );
}
